import React, { useState, useEffect } from 'react';
import { paymentConfigAPI, branchAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './PaymentConfig.css';

const PROVIDERS = [
    { value: 'Cashfree', icon: 'fa-credit-card', color: '#10b981' },
    { value: 'Razorpay', icon: 'fa-wallet', color: '#3b82f6' },
    { value: 'PhonePe', icon: 'fa-mobile-alt', color: '#7c3aed' },
    { value: 'Paytm', icon: 'fa-money-bill-wave', color: '#0ea5e9' },
];

const PaymentConfig = () => {
    const { selectedBranch } = useBranchStore();
    const [configs, setConfigs] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [configToDelete, setConfigToDelete] = useState(null);

    const [formData, setFormData] = useState({
        provider: 'Cashfree',
        environment: 'Sandbox',
        apiKey: '',
        apiSecret: '',
        saltIndex: '',
        webhookSecret: '',
        isActive: false,
        branchId: '',
    });

    useEffect(() => {
        fetchConfigs();
        fetchBranches();
    }, [selectedBranch]);

    const fetchConfigs = async () => {
        try {
            setLoading(true);
            const params = selectedBranch ? { branchId: selectedBranch._id } : undefined;
            const res = await paymentConfigAPI.getAll(params);
            setConfigs(res.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch payment gateway configs');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBranches = async () => {
        try {
            const res = await branchAPI.getAll();
            setBranches(res.data.data.branches || []);
        } catch (error) {
            console.error('Failed to fetch branches:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const resetForm = () => {
        setFormData({
            provider: 'Cashfree',
            environment: 'Sandbox',
            apiKey: '',
            apiSecret: '',
            saltIndex: '',
            webhookSecret: '',
            isActive: false,
            branchId: selectedBranch?._id || '',
        });
    };

    const handleNew = () => {
        setEditingConfig(null);
        resetForm();
        setShowModal(true);
    };

    const handleEdit = (config) => {
        setEditingConfig(config);
        setFormData({
            provider: config.provider,
            environment: config.environment || 'Sandbox',
            apiKey: config.apiKey || '',
            apiSecret: '', // never pre-filled — backend keeps existing secret unless a new one is typed
            saltIndex: config.saltIndex || '',
            webhookSecret: config.webhookSecret || '',
            isActive: !!config.isActive,
            branchId: config.branchId?._id || config.branchId || '',
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.apiKey.trim()) return toast.warning('API Key is required');
        if (!editingConfig && !formData.apiSecret.trim()) return toast.warning('API Secret is required');

        try {
            if (editingConfig) {
                await paymentConfigAPI.update(editingConfig._id, formData);
                toast.success('Payment gateway updated');
            } else {
                await paymentConfigAPI.create(formData);
                toast.success('Payment gateway added');
            }
            setShowModal(false);
            setEditingConfig(null);
            resetForm();
            fetchConfigs();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
            console.error(error);
        }
    };

    const handleDelete = (config) => {
        setConfigToDelete(config);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!configToDelete) return;
        try {
            await paymentConfigAPI.delete(configToDelete._id);
            toast.success('Payment gateway removed');
            fetchConfigs();
            setShowDeleteModal(false);
            setConfigToDelete(null);
        } catch (error) {
            toast.error('Failed to delete config');
            console.error(error);
        }
    };

    const providerMeta = (provider) => PROVIDERS.find(p => p.value === provider) || PROVIDERS[0];

    // Group configs: "Tenant-wide" first, then one section per branch that has its own config
    const configsByScope = configs.reduce((groups, config) => {
        const key = config.branchId?._id || config.branchId || 'tenant-wide';
        const label = config.branchId?.name || (config.branchId ? 'Branch' : 'Tenant-wide (all branches)');
        if (!groups[key]) groups[key] = { label, configs: [] };
        groups[key].configs.push(config);
        return groups;
    }, {});
    const scopeOrder = Object.keys(configsByScope).sort((a, b) => (a === 'tenant-wide' ? -1 : b === 'tenant-wide' ? 1 : 0));

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-credit-card"></i>
                            Payment Gateways
                        </h1>
                        <p className="page-subtitle-modern">Configure payment providers used for ISP plan purchases</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={handleNew}>
                            <i className="fas fa-plus-circle"></i>
                            Add Gateway
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading payment gateways...</p>
                </div>
            ) : configs.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <i className="fas fa-credit-card"></i>
                    </div>
                    <h3>No Payment Gateways Configured</h3>
                    <p>Add Cashfree, Razorpay, PhonePe, or Paytm to accept ISP plan payments.</p>
                </div>
            ) : (
                <div className="plans-by-branch">
                    {scopeOrder.map(scopeKey => {
                        const group = configsByScope[scopeKey];
                        return (
                            <div key={scopeKey} className="branch-plan-section">
                                <h3 className="branch-section-title">
                                    <i className={`fas ${scopeKey === 'tenant-wide' ? 'fa-globe' : 'fa-building'}`}></i>
                                    {group.label}
                                    <span className="branch-plan-count">{group.configs.length} gateway{group.configs.length !== 1 ? 's' : ''}</span>
                                </h3>
                                <div className="resource-grid-modern">
                                    {group.configs.map(config => {
                                        const meta = providerMeta(config.provider);
                                        return (
                                            <div key={config._id} className="resource-card-modern">
                                                <div className="card-header">
                                                    <div className="resource-avatar" style={{ background: `${meta.color}15`, color: meta.color }}>
                                                        <i className={`fas ${meta.icon}`}></i>
                                                    </div>
                                                    <div className="resource-title">
                                                        <h3>{config.provider}</h3>
                                                        <span className="resource-subtitle">{config.environment}</span>
                                                    </div>
                                                    <div className="card-actions">
                                                        <button className="action-btn-icon edit" onClick={() => handleEdit(config)}>
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button className="action-btn-icon delete" onClick={() => handleDelete(config)}>
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    <div className="status-badges">
                                                        <span className={`status-badge ${config.isActive ? 'active' : 'inactive'}`}>
                                                            <span className="badge-dot"></span>
                                                            {config.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                        <span className="status-badge inactive">{config.environment}</span>
                                                    </div>
                                                    <div className="info-grid">
                                                        <div className="info-item">
                                                            <i className="fas fa-key"></i>
                                                            <div>
                                                                <span className="info-label">API Key</span>
                                                                <span className="info-value">{config.apiKey ? `${config.apiKey.slice(0, 6)}••••••` : '—'}</span>
                                                            </div>
                                                        </div>
                                                        <div className="info-item">
                                                            <i className="fas fa-lock"></i>
                                                            <div>
                                                                <span className="info-label">API Secret</span>
                                                                <span className="info-value">••••••••</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>{editingConfig ? 'Edit Payment Gateway' : 'Add Payment Gateway'}</h2>
                                <p className="page-subtitle-modern" style={{ fontSize: '0.9rem', margin: 0 }}>Used for ISP plan purchase checkout</p>
                            </div>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-section">
                                    <div className="form-grid">
                                        <div className="form-group-modern">
                                            <label>Provider <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                            <select
                                                name="provider"
                                                value={formData.provider}
                                                onChange={handleInputChange}
                                                disabled={!!editingConfig}
                                            >
                                                {PROVIDERS.map(p => (
                                                    <option key={p.value} value={p.value}>{p.value}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Environment</label>
                                            <select
                                                name="environment"
                                                value={formData.environment}
                                                onChange={handleInputChange}
                                            >
                                                <option value="Sandbox">Sandbox (Test)</option>
                                                <option value="Production">Production (Live)</option>
                                            </select>
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label>Branch</label>
                                            <select
                                                name="branchId"
                                                value={formData.branchId}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Tenant-wide (all branches without their own config)</option>
                                                {branches.map(branch => (
                                                    <option key={branch._id} value={branch._id}>{branch.name}</option>
                                                ))}
                                            </select>
                                            <p className="input-hint">
                                                Leave as "Tenant-wide" to share one {formData.provider} config across all branches. Select a specific branch to override it just for that branch — useful when each location settles to a different bank account.
                                            </p>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>
                                                {formData.provider === 'Razorpay' ? 'Key ID' : formData.provider === 'PhonePe' ? 'Client ID' : 'API Key / Merchant ID'}
                                                <span style={{ color: 'var(--danger-color)' }}> *</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="apiKey"
                                                value={formData.apiKey}
                                                onChange={handleInputChange}
                                                placeholder="Enter API key / client ID"
                                                required
                                            />
                                        </div>
                                        <div className="form-group-modern">
                                            <label>
                                                {formData.provider === 'Razorpay' ? 'Key Secret' : formData.provider === 'PhonePe' ? 'Client Secret' : 'API Secret'}
                                                {!editingConfig && <span style={{ color: 'var(--danger-color)' }}> *</span>}
                                            </label>
                                            <input
                                                type="password"
                                                name="apiSecret"
                                                value={formData.apiSecret}
                                                onChange={handleInputChange}
                                                placeholder={editingConfig ? 'Leave blank to keep existing secret' : 'Enter API secret'}
                                            />
                                        </div>
                                        {formData.provider === 'PhonePe' && (
                                            <div className="form-group-modern">
                                                <label>Client Version</label>
                                                <input
                                                    type="text"
                                                    name="saltIndex"
                                                    value={formData.saltIndex}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g. 1"
                                                />
                                                <p className="input-hint">PhonePe OAuth client_version (defaults to "1" if blank).</p>
                                            </div>
                                        )}
                                        <div className="form-group-modern">
                                            <label>Webhook Secret</label>
                                            <input
                                                type="password"
                                                name="webhookSecret"
                                                value={formData.webhookSecret}
                                                onChange={handleInputChange}
                                                placeholder="Optional — for webhook signature verification"
                                            />
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    name="isActive"
                                                    checked={formData.isActive}
                                                    onChange={handleInputChange}
                                                />
                                                <span>Set as Active</span>
                                            </label>
                                            <p className="input-hint">
                                                Only one active config per provider is used at checkout. Activating this will deactivate any other {formData.provider} config.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern">
                                    <i className="fas fa-check"></i>
                                    {editingConfig ? 'Update Gateway' : 'Add Gateway'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <h2>Remove Payment Gateway</h2>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div style={{ padding: '1rem', background: '#fef2f2', borderRadius: '8px', color: '#991b1b' }}>
                                <p>Are you sure you want to remove the <strong>{configToDelete?.provider}</strong> ({configToDelete?.environment}) configuration?</p>
                                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                                    ISP plan purchases using this provider will stop working until reconfigured.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-danger-modern" onClick={confirmDelete}>
                                <i className="fas fa-trash"></i>
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentConfig;
